import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, IndianRupee, Pencil, Check, X } from 'lucide-react';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

/**
 * Per-venue rental / add-on catalogue manager (admin/superadmin).
 * Prices are per-venue and set here; booking lines snapshot the price at
 * add-time so editing here never alters historical bookings.
 */
export const RentalCatalogue = ({ facilityId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getAddonCatalogue(facilityId, false);
      setItems(res || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { if (facilityId) load(); /* eslint-disable-next-line */ }, [facilityId]);

  const handleCreate = async () => {
    if (!newName.trim() || newPrice === '') return;
    setBusy(true);
    try {
      await api.createAddonItem({ venue_id: facilityId, name: newName.trim(), unit_price: parseFloat(newPrice) });
      setNewName(''); setNewPrice('');
      await load();
      toast({ title: 'Rental item added' });
    } catch (e) {
      toast({ title: 'Could not add item', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const startEdit = (it) => { setEditId(it.id); setEditName(it.name); setEditPrice(String(it.unit_price)); };

  const saveEdit = async (id) => {
    setBusy(true);
    try {
      await api.updateAddonItem(id, { name: editName.trim(), unit_price: parseFloat(editPrice) });
      setEditId(null);
      await load();
      toast({ title: 'Updated' });
    } catch (e) {
      toast({ title: 'Could not update', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const toggleActive = async (it) => {
    setBusy(true);
    try {
      if (it.is_active) await api.deactivateAddonItem(it.id);
      else await api.updateAddonItem(it.id, { is_active: true });
      await load();
    } catch (e) {
      toast({ title: 'Action failed', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="rental-catalogue-card">
      <CardHeader>
        <CardTitle className="flex items-center">
          <IndianRupee className="mr-2 h-5 w-5" />
          Rental Catalogue
        </CardTitle>
        <CardDescription>Add-on items (e.g. Racket Rental) and their per-venue prices. Used at the counter when adding rentals to a booking.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : (
          <div className="space-y-2">
            {items.length === 0 && <p className="text-sm text-gray-400" data-testid="catalogue-empty">No rental items yet.</p>}
            {items.map((it) => (
              <div key={it.id} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg" data-testid={`catalogue-item-${it.id}`}>
                {editId === it.id ? (
                  <>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 flex-1" data-testid="catalogue-edit-name" />
                    <Input type="number" min={0} value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="h-8 w-24" data-testid="catalogue-edit-price" />
                    <Button size="sm" className="h-8 px-2" onClick={() => saveEdit(it.id)} disabled={busy} data-testid="catalogue-save-edit"><Check className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditId(null)}><X className="w-4 h-4" /></Button>
                  </>
                ) : (
                  <>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{it.name}</p>
                      <p className="text-xs text-gray-500">₹{it.unit_price}</p>
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${it.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                      {it.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => startEdit(it)} data-testid={`catalogue-edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => toggleActive(it)} disabled={busy} data-testid={`catalogue-toggle-${it.id}`}>
                      {it.is_active ? <Trash2 className="w-4 h-4 text-red-500" /> : <Check className="w-4 h-4 text-green-600" />}
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2 pt-2 border-t">
          <div className="flex-1">
            <label className="text-xs text-gray-500">Item name</label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Racket Rental" className="h-9" data-testid="catalogue-new-name" />
          </div>
          <div className="w-28">
            <label className="text-xs text-gray-500">Price (₹)</label>
            <Input type="number" min={0} value={newPrice} onChange={(e) => setNewPrice(e.target.value)} placeholder="100" className="h-9" data-testid="catalogue-new-price" />
          </div>
          <Button size="sm" className="h-9" onClick={handleCreate} disabled={busy || !newName.trim() || newPrice === ''} data-testid="catalogue-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RentalCatalogue;
